/** TanStack Query hooks for study sessions: create, submit responses (with NDJSON streaming), complete, and abandon. */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { toast } from 'sonner';
import { api } from '@/shared/api/client';
import { streamNdjson } from '@/shared/utils/streamNdjson';
import type {
  SubmitResponsePayload,
  SubmitResponseResultFull,
  GradingPhaseEvent,
  FlushStepTimePayload,
} from '@formency/shared';
import type { StudySessionWithSteps, CompleteSessionResult } from '@/shared/types';
import { continueKeys } from './useContinue';

// Re-export types so existing consumers keep working
export type { StudySessionWithSteps, CompleteSessionResult } from '@/shared/types';

// ─── Query key factory ─────────────────────────────────────────────────────

export const sessionKeys = {
  all: ['f', 'sessions'] as const,
  active: () => [...sessionKeys.all, 'active'] as const,
  detail: (id: string) => [...sessionKeys.all, 'detail', id] as const,
  summary: (id: string) => [...sessionKeys.all, 'summary', id] as const,
};

// ─── Queries ───────────────────────────────────────────────────────────────

/** Fetch the currently active session with steps and responses. */
export function useActiveSession(options?: { enabled?: boolean; staleTime?: number }) {
  return useQuery<StudySessionWithSteps | null>({
    queryKey: sessionKeys.active(),
    queryFn: () =>
      api.get<StudySessionWithSteps | null>('/me/study-sessions/active', {
        include: 'steps,responses',
      }),
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    refetchIntervalInBackground: false,
    ...options,
  });
}

/** Fetch session summary after completion. */
export function useSessionSummary(sessionId: string | undefined) {
  return useQuery<StudySessionWithSteps>({
    queryKey: sessionKeys.summary(sessionId!),
    queryFn: () => api.get<StudySessionWithSteps>(`/me/study-sessions/${sessionId}/summary`),
    enabled: !!sessionId,
  });
}

// ─── Mutations ─────────────────────────────────────────────────────────────

/** Create a learn session for a specific lesson. */
export function useCreateLearnSession() {
  const queryClient = useQueryClient();

  return useMutation<StudySessionWithSteps, Error, { lessonId: string; courseId?: string }>({
    mutationFn: (data) => api.post<StudySessionWithSteps>('/me/study-sessions/learn', data),
    onSuccess: (session) => {
      queryClient.setQueryData(sessionKeys.active(), session);
      queryClient.invalidateQueries({ queryKey: continueKeys.all });
    },
    onError: (error) => {
      if (error instanceof AxiosError && error.response?.status === 409) {
        toast.error('You have an active session. Please complete or abandon it first.');
      }
    },
  });
}

/** Create a revision session across one or more lessons, or scoped to a set of curriculum nodes. */
export function useCreateReviseSession() {
  const queryClient = useQueryClient();

  return useMutation<
    StudySessionWithSteps,
    Error,
    {
      lessonIds: string[];
      questionTypes?: string[];
      maxQuestions?: number;
      stepVersionIds?: string[];
      /**
       * Curriculum node IDs — server resolves to step versions the learner
       * can currently revise. Used by the CoachCard topic suggestions.
       */
      nodeIds?: string[];
    }
  >({
    mutationFn: (data) => api.post<StudySessionWithSteps>('/me/study-sessions/revise', data),
    onSuccess: (session) => {
      queryClient.setQueryData(sessionKeys.active(), session);
      queryClient.invalidateQueries({ queryKey: continueKeys.all });
    },
    onError: (error) => {
      if (error instanceof AxiosError && error.response?.status === 409) {
        toast.error('You have an active session. Please complete or abandon it first.');
      }
    },
  });
}

/**
 * Submit a response with NDJSON streaming for AI grading phases.
 *
 * Provides a `gradingPhase` state value that updates in real time as the
 * server streams grading progress events. The mutation resolves with the
 * final `SubmitResponseResultFull` when the `result` event arrives.
 */
export function useSubmitResponse(options?: {
  onGradingPhase?: (event: GradingPhaseEvent) => void;
}) {
  const queryClient = useQueryClient();
  const [gradingPhase, setGradingPhase] = useState<GradingPhaseEvent | null>(null);

  const mutation = useMutation<
    SubmitResponseResultFull,
    Error,
    { sessionId: string; position: number; payload: SubmitResponsePayload }
  >({
    mutationFn: async ({ sessionId, position, payload }) => {
      let finalResult: SubmitResponseResultFull | null = null;

      await streamNdjson<Record<string, unknown>>(
        `/me/study-sessions/${sessionId}/steps/${position}/responses`,
        { method: 'POST', body: JSON.stringify(payload) },
        {
          onEvent: (parsed) => {
            if (parsed.event === 'result') {
              finalResult = parsed.data as SubmitResponseResultFull;
            } else {
              const phaseEvent = parsed as unknown as GradingPhaseEvent;
              setGradingPhase(phaseEvent);
              options?.onGradingPhase?.(phaseEvent);
            }
          },
          onError: (message) => {
            throw new Error(message || 'Something went wrong');
          },
        }
      );

      if (!finalResult) throw new Error('Stream ended without result');
      return finalResult;
    },
    onSuccess: () => {
      setGradingPhase(null);
      queryClient.invalidateQueries({ queryKey: ['f', 'stats'] });
    },
    onError: () => {
      setGradingPhase(null);
    },
  });

  return { ...mutation, gradingPhase };
}

/** Complete a session and clear the active session cache. */
export function useCompleteSession() {
  const queryClient = useQueryClient();

  return useMutation<CompleteSessionResult, Error, string>({
    mutationFn: (sessionId) =>
      api.post<CompleteSessionResult>(`/me/study-sessions/${sessionId}/complete`),
    onSuccess: async () => {
      // Clear the active session cache immediately so SessionGuardProvider
      // doesn't see stale data and show a false "abandon" dialog when
      // navigating to the next lesson. The completion screen preserves
      // session data independently via stableSession in useSessionLauncher.
      await queryClient.cancelQueries({ queryKey: sessionKeys.active() });
      queryClient.setQueryData(sessionKeys.active(), null);
      queryClient.invalidateQueries({ queryKey: ['f', 'stats'] });
      queryClient.invalidateQueries({ queryKey: ['f', 'enrolments'] });
      queryClient.invalidateQueries({ queryKey: ['f', 'revise'] });
      queryClient.invalidateQueries({ queryKey: continueKeys.all });
    },
  });
}

/** Abandon a session and clear the active session cache. */
export function useAbandonSession() {
  const queryClient = useQueryClient();

  return useMutation<{ id: string; status: string }, Error, string>({
    mutationFn: (sessionId) =>
      api.post<{ id: string; status: string }>(`/me/study-sessions/${sessionId}/abandon`),
    onSuccess: async () => {
      // Cancel in-flight refetches so stale responses don't overwrite the null
      await queryClient.cancelQueries({ queryKey: sessionKeys.active() });
      queryClient.setQueryData(sessionKeys.active(), null);
      queryClient.invalidateQueries({ queryKey: ['f', 'revise'] });
      queryClient.invalidateQueries({ queryKey: continueKeys.all });
    },
  });
}

/** Flush accumulated step time entries to the server. */
export function useFlushStepTime() {
  return useMutation<
    { inserted: number },
    Error,
    { sessionId: string; payload: FlushStepTimePayload }
  >({
    mutationFn: ({ sessionId, payload }) =>
      api.post<{ inserted: number }>(`/me/study-sessions/${sessionId}/step-time`, payload),
  });
}

/** Submit a star rating for a completed session (fire-and-forget, optimistic). */
export function useSubmitSessionRating() {
  return useMutation<{ rating: number }, Error, { sessionId: string; rating: number }>({
    mutationFn: ({ sessionId, rating }) =>
      api.patch<{ rating: number }>(`/me/study-sessions/${sessionId}/feedback`, { rating }),
  });
}

/** Submit optional text feedback for a completed session. */
export function useSubmitSessionFeedback() {
  return useMutation<{ feedback: string }, Error, { sessionId: string; feedback: string }>({
    mutationFn: ({ sessionId, feedback }) =>
      api.patch<{ feedback: string }>(`/me/study-sessions/${sessionId}/feedback`, { feedback }),
  });
}
